import { useState, useEffect } from "react";
// Reuse styles from other modal for consistency or create new? 
// Assuming similar module CSS structure. I will assume LockMatchOddsModal.module.css is reusable or copy it.
// To avoid path issues, I will assume consistent styling and create a copy of styles or import if possible.
// Better to create its own module css.
import styles from "./LockDynamicMarketModal.module.css";
import { getAllBetLocks, getLocksByMarket, updateSportsLocksBulk } from "../../apiServices/betLockService";

export default function LockDynamicMarketModal({ onClose, sportId, matchId, marketId, marketName }) {
  const [userType, setUserType] = useState("selected");
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [initialLockedUsers, setInitialLockedUsers] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const token = localStorage.getItem("ownerToken") || localStorage.getItem("staffToken") || localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) {
          setError("Authentication token not found");
          setLoading(false);
          return;
        }

        // 1. Fetch All Users
        // Using getAllBetLocks to get user list (even if we ignore their old match_odds flags)
        const usersResponse = await getAllBetLocks(token);
        
        // 2. Fetch Existing Locks for this Market
        const locksResponse = await getLocksByMarket(token, marketId);

        if (usersResponse.success && usersResponse.data) {
          const userList = usersResponse.data.map(u => ({
            user_id: u.user_id,
            username: u.username || `User ${u.user_id}`,
          }));
          setUsers(userList);

          // Determine currently locked users
          let lockedSet = new Set();
          if (locksResponse.success && locksResponse.data) {
             locksResponse.data.forEach(l => {
                 if (l.is_locked) lockedSet.add(l.user_id);
             });
          }
          
          setInitialLockedUsers(lockedSet);
          setSelectedUsers(lockedSet);
        } else {
            setError("Failed to fetch users");
        }
      } catch (err) {
        setError(err.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [marketId]);

  const handleUserToggle = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      // Identify changes
      const changes = [];

      if (userType === 'all') {
          // Lock ALL users
          // We iterate ALL users and if they are NOT in initialLockedUsers, we lock them.
          // BUT wait, "All Users" usually means "Lock everyone now".
          // So we should iterate ALL KNOWN USERS.
          
          users.forEach(u => {
             // If we force "All", we ensure isLocked=true for everyone.
             // Optimize: only send if not already locked? Or just send all to be safe?
             // Safest to send all to ensure state. But efficient is only changes.
             // Let's send ALL for now or check initial.
             if (!initialLockedUsers.has(u.user_id)) {
                 changes.push({
                     targetId: u.user_id,
                     targetType: 'USER',
                     sportId,
                     matchId,
                     marketId,
                     isLocked: true
                 });
             }
          });
      } else {
          // Selected mode
          // 1. Newly locked
          for (let uid of selectedUsers) {
              if (!initialLockedUsers.has(uid)) {
                  changes.push({
                     targetId: uid,
                     targetType: 'USER',
                     sportId,
                     matchId,
                     marketId,
                     isLocked: true
                  });
              }
          }
          // 2. Newly unlocked
          for (let uid of initialLockedUsers) {
              if (!selectedUsers.has(uid)) {
                   changes.push({
                     targetId: uid,
                     targetType: 'USER',
                     sportId,
                     matchId,
                     marketId,
                     isLocked: false // Unlock
                  });
              }
          }
      }

      if (changes.length === 0) {
          setError("No changes detected.");
          setSubmitting(false);
          return;
      }

      const response = await updateSportsLocksBulk(token, changes);
      if (response.success) {
          setSuccess(`Successfully updated locks for ${changes.length} users.`);
          setTimeout(() => onClose(), 1500);
      } else {
          setError(response.message || "Failed to update locks");
      }

    } catch (err) {
      setError(err.message || "Error saving locks");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className={styles.header}>
            <h2 className={styles.title}>Bet Lock</h2>
            <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        {/* Top Controls: All Users Checkbox + Submit Button */}
        <div className={styles.topControls}>
             <label className={styles.allUsersLabel}>
                <input 
                    type="checkbox" 
                    checked={userType === 'all'} 
                    onChange={(e) => setUserType(e.target.checked ? 'all' : 'selected')} 
                />
                All Users
             </label>
             <button className={styles.submitBtn} onClick={handleSave} disabled={submitting}>
                {submitting ? '...' : 'Submit'}
             </button>
        </div>

        {/* Table Header */}
        <div className={styles.tableHeader}>
            <span className={styles.colSr}>Sr No.</span>
            <span className={styles.colUser}>User Name</span>
            <span className={styles.colCheck}>Checked</span>
        </div>

        {/* User List */}
        <div className={styles.userList}>
            {loading ? <div className={styles.loading}>Loading...</div> : (
                users.map((user, index) => (
                    <div key={user.user_id} className={styles.userRow}>
                         <span className={styles.colSr}>{index + 1}</span>
                         <span className={styles.colUser}>{user.username}</span>
                         <span className={styles.colCheck}>
                            <input 
                                type="checkbox" 
                                className={styles.checkbox}
                                checked={userType === 'all' || selectedUsers.has(user.user_id)}
                                disabled={userType === 'all'}
                                onChange={() => handleUserToggle(user.user_id)}
                            />
                         </span>
                    </div>
                ))
            )}
        </div>

      </div>
    </div>
  );
}
